'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setPendingApproval(false);

    try {
      const result = await signIn(data.email, data.password);

      // signIn returns { session, user } — use the user directly, no polling needed
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();

      const userId = result?.user?.id;
      let profileData: { role: string; member_status: string } | null = null;

      if (userId) {
        const { data: pd } = await supabase
          .from('user_profiles')
          .select('role, member_status')
          .eq('id', userId)
          .single();
        if (pd) profileData = pd;
      }

      if (!profileData) {
        toast.success('Welcome back! Redirecting...');
        await new Promise((r) => setTimeout(r, 400));
        router.push('/student-dashboard');
        return;
      }

      if (profileData.member_status === 'pending') {
        await supabase.auth.signOut();
        setPendingApproval(true);
        setIsLoading(false);
        return;
      }

      if (profileData.member_status === 'deactivated') {
        await supabase.auth.signOut();
        setError('root', { message: 'Your account has been deactivated. Contact the club lead.' });
        setIsLoading(false);
        return;
      }

      const isAdmin = profileData.role === 'lead' || profileData.role === 'core';
      toast.success(`Welcome back! Redirecting to ${isAdmin ? 'Admin' : 'Student'} dashboard...`);
      await new Promise((r) => setTimeout(r, 400));
      router.push(isAdmin ? '/member-management' : '/student-dashboard');
    } catch (err: any) {
      const msg = err?.message || '';
      if (
        msg.toLowerCase().includes('invalid login') ||
        msg.toLowerCase().includes('invalid credentials') ||
        msg.toLowerCase().includes('email not confirmed')
      ) {
        setError('root', { message: 'Invalid email or password. Please check your credentials.' });
      } else {
        setError('root', { message: msg || 'Sign in failed. Please try again.' });
      }
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-700" style={{ color: 'var(--foreground)' }}>
          Welcome back
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
          Sign in to your AWS Club account
        </p>
      </div>

      {/* Pending approval banner */}
      {pendingApproval && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4 fade-in"
          style={{ background: 'var(--warning-bg)', border: '1px solid rgba(245,158,11,0.3)' }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="2"
            className="mt-0.5 flex-shrink-0"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <p className="text-sm font-600" style={{ color: '#f59e0b' }}>
              Account awaiting approval
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              Your registration is under review by the club lead. You will be notified once approved.
            </p>
          </div>
        </div>
      )}

      {/* Root error */}
      {errors.root && (
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-xl mb-4 fade-in"
          style={{ background: 'var(--negative-bg)', border: '1px solid rgba(239,68,68,0.3)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <p className="text-sm" style={{ color: '#ef4444' }}>
            {errors.root.message}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label
            htmlFor="login-email"
            className="block text-sm font-500 mb-1.5"
            style={{ color: 'var(--foreground)' }}
          >
            Email Address
          </label>
          <input
            id="login-email"
            type="email"
            placeholder="you@college.edu.in"
            className="w-full px-4 py-2.5 rounded-xl text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.email ? 'var(--negative)' : 'var(--border)'}`,
              color: 'var(--foreground)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = errors.email ? 'var(--negative)' : 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label
            htmlFor="login-password"
            className="block text-sm font-500 mb-1.5"
            style={{ color: 'var(--foreground)' }}
          >
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full px-4 py-2.5 pr-11 rounded-xl text-sm transition-all duration-150"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.password ? 'var(--negative)' : 'var(--border)'}`,
                color: 'var(--foreground)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = errors.password ? 'var(--negative)' : 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Minimum 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me + Forgot password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded"
              style={{ accentColor: 'var(--primary)' }}
              {...register('rememberMe')}
            />
            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Remember me
            </span>
          </label>
          <a
            href="/forgot-password"
            className="text-sm font-500 transition-colors"
            style={{ color: 'var(--secondary-foreground)' }}
          >
            Forgot password?
          </a>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-xl text-sm font-600 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          style={{ minHeight: '44px' }}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-sm text-center mt-4" style={{ color: 'var(--muted-foreground)' }}>
        New to AWS Club?{' '}
        <button
          onClick={onSwitchToRegister}
          className="font-600"
          style={{ color: 'var(--secondary-foreground)' }}
        >
          Register now
        </button>
      </p>
    </div>
  );
}